/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { accountClient } from '@/services/cloudClient/accountClient'
import { PRIVATE_OFFER_STATUS, PrivateOfferDetail as PrivateOfferDetailType, PrivateOfferStatus } from '@/types/privateOffer'
import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { PlatformRespondToPrivateOfferRequestActionEnum } from 'account-client'
import { App, Button, Card, Col, Descriptions, Form, Input, Modal, Row, Space, Spin, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import '../styles.less'
import { AttachmentList } from './AttachmentList'
import { OfferPricingInfo } from './OfferPricingInfo'
import { RewriteParentOffer } from './RewriteParentOffer'

const { Title, Text } = Typography

// Extended Private Offer type, including contract buyer information
interface ExtendedPrivateOffer extends PrivateOfferDetailType {
  contractBuyer?: {
    enterprise?: {
      name?: string
      socialID?: string
      juridicalPerson?: string
      address?: string
      contactPerson?: string
      contactEmail?: string
      contactPhone?: string
    }
    citizen?: {
      name?: string
      socialID?: string
    }
    legalEntityType?: string
  }
  contractInfo?: {
    id?: string
    buyerType?: string
    tags?: string
    serviceType?: string
    updatedAt?: string
    buyerName?: string
    name?: string
  }
  serviceType?: string
  parentOfferId?: string
  parentOfferOldExpirationDate?: number
  parentOfferNewExpirationDate?: number
}

export function PrivateOfferDetail() {
  const { contractId, offerId } = useParams<{ contractId: string; offerId: string }>()
  const navigate = useNavigate()

  const { modal, message } = App.useApp()

  if (!contractId || !offerId) {
    throw new Error('Contract ID and Offer ID are required')
  }
  const { t } = useTranslation()

  const handleBack = () => {
    navigate(`/billing-center/${contractId}/private-offers`)
  }

  const [offerDetail, setOfferDetail] = useState<ExtendedPrivateOffer | null>(null)
  const [loading, setLoading] = useState(true)
  const [rejectModalVisible, setRejectModalVisible] = useState(false)
  const [rejectForm] = Form.useForm()

  const fetchOfferDetail = async () => {
    if (!contractId || !offerId) return

    setLoading(true)

    // Get offer details, contract buyer information, and contract info in parallel
    const [offerResponse, contractBuyerResponse, contractResponse] = await Promise.all([
      accountClient.privateOffersApi.apiV1ContractsIdPrivateOffersPoidGet(contractId, offerId),
      accountClient.contractsApi.getContractBuyer(contractId),
      accountClient.contractsApi.getContract(contractId)
    ])

    // Merge data and filter out sensitive approver information
    const offerData = offerResponse.data
    const contractBuyer = contractBuyerResponse.data
    const contractInfo = contractResponse.data

    // Filter out sensitive internal employee information for customer-facing API
    const {
      approverId,
      approverName,
      approvedAt,
      approvalComment,
      creatorName,  // 移除创建者姓名
      creatorId,    // 移除创建者ID
      ...filteredOfferData
    } = offerData

    // 映射API返回的type字段到itemType字段，确保前端组件能正确显示
    const mappedOfferItems = filteredOfferData.offerItems?.map((item: any) => ({
      ...item,
      itemType: item.type || item.itemType // API返回的是type，前端期望的是itemType
    })) || []

    setOfferDetail({
      ...filteredOfferData,
      offerItems: mappedOfferItems,
      contractBuyer: contractBuyer,
      serviceType: contractInfo.serviceType || 'saas',
      contractInfo: contractInfo
    })

    setLoading(false)
  }

  useEffect(() => {
    fetchOfferDetail()
  }, [contractId, offerId])

  // Format timestamp to readable date
  const formatTimestamp = (timestamp: number | undefined): string => {
    if (!timestamp) return '-'
    return dayjs(timestamp * 1000).format('YYYY-MM-DD') // Convert to milliseconds
  }

  // Format ISO string to readable date
  const formatISOString = (isoString: string | undefined): string => {
    if (!isoString) return '-'
    return dayjs(isoString).format('YYYY-MM-DD HH:mm')
  }

  if (loading) {
    return (
      <LayoutOthers.MainContent title={t('Private Offer Detail')}>
        <Spin size="large" style={{ display: 'block', textAlign: 'center', paddingTop: 100 }} />
      </LayoutOthers.MainContent>
    )
  }

  if (!offerDetail) {
    return (
      <LayoutOthers.MainContent title={t('Private Offer Detail')}>
        <Card>
          <div style={{ textAlign: 'center', padding: 50 }}>
            <p>{t('Offer details not found')}</p>
          </div>
        </Card>
      </LayoutOthers.MainContent>
    )
  }

  const getStatusConfig = (status: PrivateOfferStatus | undefined) => {
    if (!status) return { color: 'default', text: t('Unknown') }

    // 统一状态颜色映射规范，与列表页和管理端完全一致
    const statusMap: Record<PrivateOfferStatus, { color: string, text: string }> = {
      [PRIVATE_OFFER_STATUS.DRAFT]: { color: 'default', text: t('Draft') }, // 默认色
      [PRIVATE_OFFER_STATUS.PENDING_APPROVAL]: { color: 'processing', text: t('Pending Approval') }, // 蓝色
      [PRIVATE_OFFER_STATUS.APPROVED]: { color: 'warning', text: t('Approved') }, // 橙色
      [PRIVATE_OFFER_STATUS.PENDING_ACCEPTANCE]: { color: 'warning', text: t('Pending Acceptance') }, // 橙色
      [PRIVATE_OFFER_STATUS.ACCEPTED]: { color: 'success', text: t('Accepted') }, // 绿色
      [PRIVATE_OFFER_STATUS.REJECTED]: { color: 'error', text: t('Rejected') }, // 红色
      [PRIVATE_OFFER_STATUS.EXPIRED]: { color: 'default', text: t('Expired') }, // 灰色
      [PRIVATE_OFFER_STATUS.INVALID]: { color: 'default', text: t('Invalid') }, // 灰色
    }
    return statusMap[status as PrivateOfferStatus] || { color: 'default', text: status }
  }

  const statusConfig = getStatusConfig(offerDetail.status as PrivateOfferStatus)

  // Get Signing Entity Information
  const getCustomerInfo = (): {
    legalEntityName: string
    taxId: string
    legalRepresentative?: string
    legalRepresentativeId?: string
    address: string
    contactPerson?: string
    contactEmail: string
    contactPhone: string
    buyerType: string
  } | null => {
    const { contractBuyer, contractInfo } = offerDetail

    if (!contractBuyer || !contractInfo) {
      return null
    }

    const isEnterprise = contractInfo.buyerType === 'E'
    const isCitizen = contractInfo.buyerType === 'P'

    if (isEnterprise) {
      const enterprise = contractBuyer.enterprise
      const result = {
        legalEntityName: enterprise?.name || contractInfo.buyerName || contractInfo.name || '-',
        taxId: enterprise?.socialID || '-',
        legalRepresentative: enterprise?.juridicalPerson || '-',
        legalRepresentativeId: enterprise?.juridicalPerson || '-',
        address: enterprise?.address || '-',
        contactPerson: enterprise?.contactPerson || '-',
        contactEmail: enterprise?.contactEmail || '-',
        contactPhone: enterprise?.contactPhone || '-',
        buyerType: 'enterprise'
      }
      return result
    }

    if (isCitizen) {
      const citizen = contractBuyer.citizen
      const result = {
        legalEntityName: citizen?.name || contractInfo.buyerName || contractInfo.name || '-',
        taxId: citizen?.socialID || '-',
        address: '-', // Citizens don't have separate address in contractBuyer
        contactEmail: '-', // Citizens don't have separate contact info in contractBuyer
        contactPhone: '-', // Citizens don't have separate contact info in contractBuyer
        buyerType: 'citizen'
      }
      return result
    }

    return null
  }

  const customerInfo = getCustomerInfo()

  const acceptOffer = async () => {
    await accountClient.privateOffersApi.apiV1ContractsIdPrivateOffersPoidRespondPost(
      contractId,
      offerId,
      { action: PlatformRespondToPrivateOfferRequestActionEnum.Accept }
    )
    message.success(t('Offer accepted successfully'))
    fetchOfferDetail()
  }

  const handleAccept = () => {
    modal.confirm({
      title: t('Confirm Accept Offer'),
      content: t('accept_offer_confirm_desc'),
      okText: t('Confirm'),
      cancelText: t('Cancel'),
      okButtonProps: { type: 'primary' },
      onOk: acceptOffer,
    })
  }

  const handleRejectConfirm = async () => {
    const values = await rejectForm.validateFields()
    await accountClient.privateOffersApi.apiV1ContractsIdPrivateOffersPoidRespondPost(
      contractId,
      offerId,
      { action: PlatformRespondToPrivateOfferRequestActionEnum.Reject, comment: values.reason }
    )
    message.success(t('Offer rejected'))
    setRejectModalVisible(false)
    rejectForm.resetFields()
    fetchOfferDetail()
  }

  const handleUpdateInfo = () => {
    // 打开新窗口而不是在当前页面跳转
    window.open(`/contracts/${contractId}/overview`, '_blank')
  }

  // Determine if the update KYC information button should be displayed
  const shouldShowUpdateButton = () => {
    if (!offerDetail) return false

    // Only show update button in these states:
    // - pending_acceptance: when waiting for customer acceptance, customer might need to update information
    // - draft: when in draft state, can be modified
    // - approved: when approved but not yet sent to customer
    const allowedStatuses: PrivateOfferStatus[] = [
      PRIVATE_OFFER_STATUS.PENDING_ACCEPTANCE,
      PRIVATE_OFFER_STATUS.DRAFT,
      PRIVATE_OFFER_STATUS.APPROVED
    ]
    return allowedStatuses.includes(offerDetail.status as PrivateOfferStatus)
  }

  return (
    <div className="private-offer-container">
      <LayoutOthers.MainContent title={t('Private Offer Details')}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Back button */}
          <Card>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ padding: 0 }}
            >
              {t('Back to Private Offers')}
            </Button>
          </Card>

          {/* Page title and status */}
          {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Title level={2} style={{ margin: 0 }}>{t('Private Offer Details')}</Title>
            <Tag color={statusConfig.color} style={{ fontSize: 14, padding: '4px 12px' }}>
              {statusConfig.text}
            </Tag>
          </div> */}

          {/* 1. Offer Summary - 紧凑布局 */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div className="section-indicator" style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                marginRight: 8
              }} />
              <Title level={4} className="section-title" style={{ margin: 0 }}>
                {t('Offer Summary')}
              </Title>
            </div>
            <Descriptions column={3} bordered size="small">
              <Descriptions.Item label="Offer ID">
                {offerDetail.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('Offer Status')}>
                <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Created on')}>
                {formatISOString(offerDetail.createdAt)}
              </Descriptions.Item>
              {/* 移除Creator字段，避免泄露内部员工信息 */}
              {/* <Descriptions.Item label={t('Creator')}>
                {offerDetail.creatorName || '-'}
              </Descriptions.Item> */}
            </Descriptions>
          </Card>

          {/* 2. Signing Entity Information - 简化布局 */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div className="section-indicator" style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                marginRight: 8
              }} />
              <Title level={4} className="section-title" style={{ margin: 0 }}>
                {t('Signing Entity Information')}
              </Title>
            </div>

            {/* Signing Entity Information - 完整显示 */}
            {customerInfo ? (
              <Descriptions column={3} bordered size="small">
                <Descriptions.Item label={t('Legal Entity Name')}>
                  {customerInfo.legalEntityName}
                </Descriptions.Item>
                <Descriptions.Item label={t('Registration Number')}>
                  {customerInfo.taxId}
                </Descriptions.Item>
                <Descriptions.Item label={t('Country of Incorporation')}>
                  {offerDetail.contractBuyer?.enterprise?.juridicalPerson || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('Company Address')}>
                  {customerInfo.address}
                </Descriptions.Item>
                {customerInfo.buyerType === 'enterprise' && (
                  <Descriptions.Item label={t('Contact Person')}>
                    {customerInfo.contactPerson}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t('Contact Email')}>
                  {customerInfo.contactEmail}
                </Descriptions.Item>
                {customerInfo.buyerType === 'enterprise' && (
                  <Descriptions.Item label={t('Legal Representative')}>
                    {customerInfo.legalRepresentative}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t('Contact Phone')}>
                  {customerInfo.contactPhone}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Text className="section-text">{t('Signing Entity Information not available')}</Text>
              </div>
            )}

            {/* Update button */}
            {shouldShowUpdateButton() && (
              <div style={{ textAlign: 'right', marginTop: 12 }}>
                <Button type="primary" size="small" onClick={handleUpdateInfo}>
                  {t('Update Information')}
                </Button>
              </div>
            )}
          </Card>

          {/* 3. Pricing & Discounts - 提前显示重要信息 */}
          <OfferPricingInfo offer={offerDetail} />

          {/* 4.5. Attachments */}
          <AttachmentList contractId={contractId} offerId={offerId} />

          {/* 4. Offer Timeline - 紧凑布局 */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div className="section-indicator-success" style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 8 }} />
              <Title level={4} className="section-title" style={{ margin: 0 }}>
                {t('Offer Effective Time')}
              </Title>
            </div>
            <Row gutter={[24, 16]} style={{ margin: '0 16px' }}>
              {/* Effective Date */}
              <Col span={8}>
                <div>
                  <Text className="section-title" style={{ fontSize: 14 }}>{t('Effective Date')}</Text>
                  <div style={{ marginTop: 6 }}>
                    <Text style={{ color: 'var(--private-offer-bg-success)', fontSize: 16, fontWeight: 'bold' }}>
                      {formatTimestamp(offerDetail.effectiveDate)}
                    </Text>
                  </div>
                </div>
              </Col>

              {/* Expiration Date */}
              <Col span={8}>
                <div>
                  <Text className="section-title" style={{ fontSize: 14 }}>{t('Offer Expiration')}</Text>
                  <div style={{ marginTop: 6 }}>
                    <Text style={{
                      color: offerDetail.expirationDate && offerDetail.expirationDate * 1000 < Date.now()
                        ? '#ff4d4f'
                        : 'var(--private-offer-bg-primary)',
                      fontSize: 16,
                      fontWeight: 'bold'
                    }}>
                      {formatTimestamp(offerDetail.expirationDate)}
                    </Text>
                  </div>
                </div>
              </Col>

              {/* Confirmation Deadline */}
              {offerDetail.confirmationDeadline && (
                <Col span={8}>
                  <div>
                    <Text className="section-title" style={{ fontSize: 14 }}>{t('Confirmation Deadline')}</Text>
                    <div style={{ marginTop: 6 }}>
                      <Text style={{
                        color: offerDetail.confirmationDeadline * 1000 < Date.now()
                          ? '#ff4d4f'
                          : '#faad14',
                        fontSize: 16,
                        fontWeight: 'bold'
                      }}>
                        {formatTimestamp(offerDetail.confirmationDeadline)}
                      </Text>
                    </div>
                  </div>
                </Col>
              )}

              {/* Channel */}
              {/* {offerDetail.channel && (
                <Col span={12}>
                  <div>
                    <Text className="section-title" style={{ fontSize: 14 }}>{t('Channel')}</Text>
                    <div style={{ marginTop: 6 }}>
                      <Text style={{ fontSize: 16, fontWeight: 'bold' }}>
                        {offerDetail.channel === 'direct' ? t('Direct') : t('Marketplace')}
                      </Text>
                    </div>
                  </div>
                </Col>
              )} */}

              {/* Comment */}
              {offerDetail.comment && (
                <Col span={24}>
                  <div>
                    <Text className="section-title" style={{ fontSize: 14 }}>{t('Comment')}</Text>
                    <div style={{ marginTop: 6 }}>
                      <Text style={{ fontSize: 14 }}>
                        {offerDetail.comment}
                      </Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Card>

          {/* Rewrites Parent Offer - 仅当存在 parentOfferId 时显示 */}
          {offerDetail.parentOfferId && (
            <RewriteParentOffer
              parentOfferId={offerDetail.parentOfferId}
              parentOfferOldExpirationDate={offerDetail.parentOfferOldExpirationDate}
              parentOfferNewExpirationDate={offerDetail.parentOfferNewExpirationDate}
            />
          )}

          {/* Action buttons */}
          {offerDetail.status === PRIVATE_OFFER_STATUS.PENDING_ACCEPTANCE && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                <Button size="large" onClick={handleBack}>{t('Cancel')}</Button>
                <Button danger size="large" onClick={() => setRejectModalVisible(true)}>{t('Reject')}</Button>
                <Button type="primary" size="large" onClick={handleAccept}>{t('Accept')}</Button>
              </div>
            </Card>
          )}

          {/* Important notes */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <InfoCircleOutlined style={{ color: 'var(--private-offer-bg-primary)', marginRight: 8 }} />
              <Text strong>{t('Important Notes:')}</Text>
            </div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {offerDetail.status !== PRIVATE_OFFER_STATUS.ACCEPTED && (
                <li>{t('This private offer is valid until')} {formatTimestamp(offerDetail.expirationDate)}</li>
              )}
              <li>{t('Discounts will be applied automatically to your next billing cycle')}</li>
              <li>{t('Contact support if you have any questions about this offer')}</li>
            </ul>
          </Card>
        </Space>
      </LayoutOthers.MainContent>

      {/* Reject Modal */}
      <Modal
        title={t('Confirm Reject Offer')}
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleRejectConfirm}
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label={t('Rejection Reason')}
            rules={[{ required: true, message: t('Please enter rejection reason') }]}
          >
            <Input.TextArea rows={4} placeholder={t('Please enter rejection reason')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}